import UserPreferencesService from './userPreferencesService';
import AuthService from './authService';
import { API_BASE } from '../config/api';

class SettingsService {
  // Default settings structure
  static getDefaultSettings() {
    return {
      privacy: {
        defaultActivityVisibility: 'friends', // 'public', 'friends', 'private'
        defaultTripVisibility: 'friends',
        // Allow others to find you in user search (read server-side by
        // GET /api/social/users/search; friends can always find you).
        allowDiscovery: true,
        showActivityHistory: 'friends', // Who can see your past activities
        // Friends who add you to an outing (Who's going) get suggestions shaped
        // by your favourites. Read server-side by outingGroupService and place
        // search; a missing value means allowed.
        planWithMyFavourites: true
      },
      hardConstraints: {
        dietary: {
          allergies: [], // ['nuts', 'shellfish', 'dairy', 'gluten', 'eggs', 'soy', 'fish']
          customAllergies: '', // Free text for other allergies
          restrictions: [], // ['vegetarian', 'vegan', 'kosher', 'halal', 'pescatarian', 'keto', 'no-pork', 'no-beef']
          severity: 'moderate' // 'mild', 'moderate', 'severe' - affects filtering strictness
        },
        substances: {
          alcohol: 'social', // 'avoid' (filter out bars/breweries), 'social' (include all), 'prefer' (boost alcohol venues)
          smoking: 'avoid' // 'avoid' (smoke-free only), 'tolerant' (smoking allowed venues OK)
        }
      },
      quickGroups: [
        {
          id: 'family',
          name: 'Family',
          emoji: '👨‍👩‍👧‍👦',
          members: [],
          isDefault: true
        }
      ],
      relationships: {}, // Map of userId to relationship type (spouse, sibling, child, etc.)
      notifications: {
        activityReminders: true,
        weatherAlerts: true,
        friendActivity: true,
        groupInvitations: true,
        dailyDigest: false
      },
      location: {
        current: '', // Empty by default - user must set their location
        home: '', // User's home location for quick searches
        autoDetect: false,
        showInProfile: true
      },
      units: {
        temperature: 'auto', // 'auto' (detect from home country), 'C', or 'F'
        distance: 'auto' // 'auto' (detect from home country), 'metric', or 'imperial'
      },
      // Explicit UI-language choice ('en' | 'nl'); null = follow device/browser
      // detection. Persisted here so the preference follows the account across
      // devices; the device-level mirror lives in localStorage (languagePreference.js).
      language: null
    };
  }

  // Smart merge settings - preserves user values, only fills missing with defaults
  static smartMergeSettings(defaults, userSettings) {
    const result = {};
    
    // Iterate through all default keys
    for (const [key, defaultValue] of Object.entries(defaults)) {
      if (userSettings && userSettings.hasOwnProperty(key)) {
        // User has this setting - check if it's an object that needs deep merging
        if (typeof defaultValue === 'object' && defaultValue !== null && !Array.isArray(defaultValue)) {
          // Deep merge objects, but preserve user values
          result[key] = this.smartMergeSettings(defaultValue, userSettings[key]);
        } else {
          // Use user value as-is (even if empty string - this preserves user's explicit choice)
          result[key] = userSettings[key];
        }
      } else {
        // User doesn't have this setting - use default
        result[key] = defaultValue;
      }
    }
    
    // Also include any user settings that aren't in defaults (future-proofing)
    for (const [key, userValue] of Object.entries(userSettings || {})) {
      if (!defaults.hasOwnProperty(key)) {
        result[key] = userValue;
      }
    }
    
    return result;
  }

  // Get user settings with defaults (now async for database). Reads keep the
  // offline-first fallback: when the server can't be read this still answers
  // (device copy merged over defaults). A caller that will write the blob back
  // must use getUserSettingsWithSource and check `fromServer` instead.
  static async getUserSettings(userId = null) {
    const { settings } = await this.getUserSettingsWithSource(userId);
    return settings;
  }

  // Same as getUserSettings, plus whether the answer is the account's real
  // settings. `fromServer` is false when the preferences GET failed (or no
  // user could be resolved): `settings` is then defaults or this device's
  // fallback copy, and saving it whole would overwrite the account (BH-10-02).
  // An account that has never saved settings reads as defaults WITH
  // fromServer true — that is the server's answer.
  static async getUserSettingsWithSource(userId = null) {
    try {
      // Get current user if no userId provided
      if (!userId) {
        const currentUser = await AuthService.getCurrentUser();
        userId = currentUser?.id;
      }

      if (!userId) {
        console.warn('No user ID available, returning default settings');
        return { settings: this.getDefaultSettings(), fromServer: false };
      }

      // Try to load settings from database
      const dbPreferences = await UserPreferencesService.getUserPreferencesAsync(userId);
      const fromServer = UserPreferencesService.isServerResult(dbPreferences);
      const defaultSettings = this.getDefaultSettings();

      // Check if we have settings stored in the database under the 'settings' category
      if (dbPreferences && dbPreferences.settings && dbPreferences.settings.all) {
        // Smart merge: preserve user values, only fill missing settings with defaults
        return { settings: this.smartMergeSettings(defaultSettings, dbPreferences.settings.all), fromServer };
      }

      // Return defaults if no settings in database
      return { settings: defaultSettings, fromServer };
    } catch (error) {
      console.error('Error loading user settings from database:', error);
      // Return defaults on error
      return { settings: this.getDefaultSettings(), fromServer: false };
    }
  }

  static SETTINGS_UNAVAILABLE = 'settings_unavailable';

  // The one read-modify-write path for single-field writers: read the
  // account's settings, apply `mutate` (returns the new whole blob), save.
  // Refuses — without writing — when the read did not come from the server,
  // so one failed GET can't put defaults over the home, groups, constraints
  // and privacy choices (BH-10-02). `mutate` gets a copy it may change.
  static async updateSettingsFromServer(mutate, userId = null) {
    try {
      const { settings, fromServer } = await this.getUserSettingsWithSource(userId);
      if (!fromServer) {
        return { success: false, error: this.SETTINGS_UNAVAILABLE };
      }
      const copy = JSON.parse(JSON.stringify(settings));
      const updated = mutate(copy) || copy;
      const result = await this.saveUserSettings(updated, userId);
      return { ...result, settings: updated };
    } catch (error) {
      console.error('Error updating user settings:', error);
      return { success: false, error: error.message };
    }
  }

  // Save user settings (now async for database)
  static async saveUserSettings(settings, userId = null) {
    try {
      // Get current user if no userId provided
      if (!userId) {
        const currentUser = await AuthService.getCurrentUser();
        userId = currentUser?.id;
      }

      if (!userId) {
        console.error('Cannot save settings without user ID');
        return { success: false, error: 'No user logged in' };
      }

      // Save settings to database under 'settings' category
      const result = await UserPreferencesService.setPreferenceAsync('settings', 'all', settings, userId);
      
      if (result && result.success !== false) {
        return { success: true };
      } else {
        return { success: false, error: result?.error || 'Failed to save settings' };
      }
    } catch (error) {
      console.error('Error saving user settings to database:', error);
      return { success: false, error: error.message };
    }
  }

  // Update privacy settings
  static async updatePrivacySettings(privacyUpdates, userId = null) {
    try {
      return await this.updateSettingsFromServer((current) => ({
        ...current,
        privacy: { ...current.privacy, ...privacyUpdates }
      }), userId);
    } catch (error) {
      console.error('Error updating privacy settings:', error);
      return { success: false, error: error.message };
    }
  }

  // Add or update a quick group
  static async saveQuickGroup(groupData, userId = null) {
    try {
      return await this.updateSettingsFromServer((current) => {
        const groups = Array.isArray(current.quickGroups) ? current.quickGroups : [];
        const existingGroupIndex = groups.findIndex(g => g.id === groupData.id);
        if (existingGroupIndex >= 0) {
          groups[existingGroupIndex] = groupData;
        } else {
          groups.push(groupData);
        }
        return { ...current, quickGroups: groups };
      }, userId);
    } catch (error) {
      console.error('Error saving quick group:', error);
      return { success: false, error: error.message };
    }
  }

  // Delete a quick group
  static async deleteQuickGroup(groupId, userId = null) {
    try {
      return await this.updateSettingsFromServer((current) => ({
        ...current,
        quickGroups: (current.quickGroups || []).filter(g => g.id !== groupId)
      }), userId);
    } catch (error) {
      console.error('Error deleting quick group:', error);
      return { success: false, error: error.message };
    }
  }

  // Update dietary constraints
  static async updateDietaryConstraints(dietaryUpdates, userId = null) {
    try {
      return await this.updateSettingsFromServer((current) => ({
        ...current,
        hardConstraints: {
          ...current.hardConstraints,
          dietary: { ...current.hardConstraints.dietary, ...dietaryUpdates }
        }
      }), userId);
    } catch (error) {
      console.error('Error updating dietary constraints:', error);
      return { success: false, error: error.message };
    }
  }

  // Update substance preferences
  static async updateSubstancePreferences(substanceUpdates, userId = null) {
    try {
      return await this.updateSettingsFromServer((current) => ({
        ...current,
        hardConstraints: {
          ...current.hardConstraints,
          substances: { ...current.hardConstraints.substances, ...substanceUpdates }
        }
      }), userId);
    } catch (error) {
      console.error('Error updating substance preferences:', error);
      return { success: false, error: error.message };
    }
  }

  // Update location settings
  static async updateLocation(location, userId = null) {
    try {
      return await this.updateSettingsFromServer((current) => ({
        ...current,
        location: { ...current.location, home: location }
      }), userId);
    } catch (error) {
      console.error('Error updating location:', error);
      return { success: false, error: error.message };
    }
  }

  // Get dietary constraints
  static async getDietaryConstraints(userId = null) {
    try {
      const settings = await this.getUserSettings(userId);
      return settings.hardConstraints?.dietary || {};
    } catch (error) {
      console.error('Error getting dietary constraints:', error);
      return {};
    }
  }

  // Check if a place violates user's dietary constraints
  static async checkDietaryViolation(placeTypes, placeName, userId = null) {
    try {
      const dietary = await this.getDietaryConstraints(userId);
      
      if (!dietary.restrictions?.length && !dietary.allergies?.length) {
        return { violates: false };
      }

      const placeNameLower = (placeName || '').toLowerCase();
      const violations = [];

      // Check vegan/vegetarian violations
      if (dietary.restrictions?.includes('vegan')) {
        if (placeTypes?.includes('steakhouse') || placeTypes?.includes('bbq') || 
            placeNameLower.includes('bbq') || placeNameLower.includes('steakhouse')) {
          violations.push('Not suitable for vegan diet');
        }
      }

      if (dietary.restrictions?.includes('vegetarian')) {
        if (placeTypes?.includes('steakhouse') || placeTypes?.includes('seafood_restaurant') ||
            placeNameLower.includes('steakhouse') || placeNameLower.includes('seafood')) {
          violations.push('Limited vegetarian options');
        }
      }

      // Check religious dietary restrictions
      if (dietary.restrictions?.includes('halal')) {
        if (placeTypes?.includes('bar') || placeTypes?.includes('brewery') ||
            placeNameLower.includes('pork') || placeNameLower.includes('bacon')) {
          violations.push('May not be halal-friendly');
        }
      }

      if (dietary.restrictions?.includes('kosher')) {
        if (placeNameLower.includes('seafood') && !placeNameLower.includes('kosher')) {
          violations.push('May not be kosher');
        }
      }

      // Check for no-pork restriction
      if (dietary.restrictions?.includes('no-pork')) {
        if (placeNameLower.includes('bacon') || placeNameLower.includes('pork') || 
            placeNameLower.includes('bbq')) {
          violations.push('May serve pork');
        }
      }

      return {
        violates: violations.length > 0,
        violations,
        severity: dietary.severity || 'moderate'
      };
    } catch (error) {
      console.error('Error checking dietary violation:', error);
      return { violates: false };
    }
  }

  // Check if a place violates user's substance preferences
  static async checkSubstanceViolation(placeTypes, placeName, userId = null) {
    try {
      const settings = await this.getUserSettings(userId);
      const substances = settings.hardConstraints?.substances || {};
      
      const placeNameLower = (placeName || '').toLowerCase();
      const violations = [];

      // Check alcohol preferences
      if (substances.alcohol === 'avoid') {
        if (placeTypes?.includes('bar') || placeTypes?.includes('brewery') || 
            placeTypes?.includes('winery') || placeTypes?.includes('night_club') ||
            placeNameLower.includes('bar') || placeNameLower.includes('brewery') ||
            placeNameLower.includes('wine') || placeNameLower.includes('pub')) {
          violations.push('Primarily serves alcohol');
        }
      }

      // Check smoking preferences
      if (substances.smoking === 'avoid') {
        if (placeTypes?.includes('hookah_bar') || placeNameLower.includes('hookah') ||
            placeNameLower.includes('cigar')) {
          violations.push('Smoking venue');
        }
      }

      return {
        violates: violations.length > 0,
        violations
      };
    } catch (error) {
      console.error('Error checking substance violation:', error);
      return { violates: false };
    }
  }

  // Get visibility setting for a specific content type
  static async getVisibilitySetting(contentType, userId = null) {
    try {
      const settings = await this.getUserSettings(userId);
      
      switch(contentType) {
        case 'activity':
          return settings.privacy?.defaultActivityVisibility || 'friends';
        case 'trip':
          return settings.privacy?.defaultTripVisibility || 'friends';
        case 'history':
          return settings.privacy?.showActivityHistory || 'friends';
        default:
          return 'friends';
      }
    } catch (error) {
      console.error('Error getting visibility setting:', error);
      return 'friends';
    }
  }

  // Check if user allows discovery
  static async allowsDiscovery(userId = null) {
    try {
      const settings = await this.getUserSettings(userId);
      return settings.privacy?.allowDiscovery !== false;
    } catch (error) {
      console.error('Error checking discovery setting:', error);
      return true;
    }
  }

  // Get notification preferences
  static async getNotificationPreferences(userId = null) {
    try {
      const settings = await this.getUserSettings(userId);
      return settings.notifications || {};
    } catch (error) {
      console.error('Error getting notification preferences:', error);
      return {};
    }
  }

  // Update notification preferences
  static async updateNotificationPreferences(notificationUpdates, userId = null) {
    try {
      return await this.updateSettingsFromServer((current) => ({
        ...current,
        notifications: { ...current.notifications, ...notificationUpdates }
      }), userId);
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      return { success: false, error: error.message };
    }
  }

  // === STATIC UTILITY METHODS ===

  // Get relationship types for family members
  static getRelationshipTypes() {
    return [
      { value: 'spouse', label: 'Spouse/Partner', emoji: '💑' },
      { value: 'child', label: 'Child', emoji: '👶' },
      { value: 'parent', label: 'Parent', emoji: '👨‍👩‍👧' },
      { value: 'sibling', label: 'Sibling', emoji: '👫' },
      { value: 'grandparent', label: 'Grandparent', emoji: '👴' },
      { value: 'grandchild', label: 'Grandchild', emoji: '👶' },
      { value: 'aunt_uncle', label: 'Aunt/Uncle', emoji: '👨‍👩‍👧' },
      { value: 'cousin', label: 'Cousin', emoji: '🧑‍🤝‍🧑' },
      { value: 'friend', label: 'Close Friend', emoji: '🤝' }
    ];
  }

  // Get allergy options
  static getAllergyOptions() {
    return [
      { value: 'nuts', label: 'Tree Nuts', severity: 'high' },
      { value: 'peanuts', label: 'Peanuts', severity: 'high' },
      { value: 'shellfish', label: 'Shellfish', severity: 'high' },
      { value: 'fish', label: 'Fish', severity: 'medium' },
      { value: 'dairy', label: 'Dairy', severity: 'medium' },
      { value: 'eggs', label: 'Eggs', severity: 'medium' },
      { value: 'soy', label: 'Soy', severity: 'low' },
      { value: 'gluten', label: 'Gluten/Wheat', severity: 'medium' },
      { value: 'sesame', label: 'Sesame', severity: 'high' }
    ];
  }

  // Get dietary restriction options
  static getDietaryRestrictionOptions() {
    return [
      { value: 'vegetarian', label: 'Vegetarian', description: 'No meat or fish' },
      { value: 'vegan', label: 'Vegan', description: 'No animal products' },
      { value: 'pescatarian', label: 'Pescatarian', description: 'Fish but no meat' },
      { value: 'kosher', label: 'Kosher', description: 'Jewish dietary laws' },
      { value: 'halal', label: 'Halal', description: 'Islamic dietary laws' },
      { value: 'keto', label: 'Ketogenic', description: 'High fat, low carb' },
      { value: 'paleo', label: 'Paleo', description: 'Whole foods only' },
      { value: 'no-pork', label: 'No Pork', description: 'Avoids pork products' },
      { value: 'no-beef', label: 'No Beef', description: 'Avoids beef products' },
      { value: 'low-carb', label: 'Low Carb', description: 'Reduced carbohydrates' },
      { value: 'gluten-free', label: 'Gluten-Free', description: 'No gluten-containing grains' }
    ];
  }

  // Countries that use Fahrenheit
  static FAHRENHEIT_COUNTRIES = ['US', 'BS', 'KY', 'PW', 'FM', 'MH'];

  // Miles countries. Deliberately NOT the same list as Fahrenheit: the UK
  // uses Celsius for weather but miles for distance — which is why
  // temperature and distance are separate preferences with separate autos.
  static IMPERIAL_DISTANCE_COUNTRIES = ['US', 'GB', 'UK', 'LR', 'MM'];

  // Resolve effective temperature unit ('C' or 'F')
  // If set to 'auto', detect from home country code
  static resolveTemperatureUnit(settings, homeCountryCode = null) {
    const unit = settings?.units?.temperature || 'auto';
    if (unit === 'C' || unit === 'F') return unit;
    // Auto-detect: Fahrenheit for US and a few territories, Celsius everywhere else
    if (homeCountryCode && this.FAHRENHEIT_COUNTRIES.includes(homeCountryCode.toUpperCase())) {
      return 'F';
    }
    return 'C';
  }

  // Module-level cache for the user's home country code. It changes ~never,
  // several surfaces call getTemperatureUnit() near-simultaneously, and the
  // in-flight promise is shared so they all ride one request. Keyed by auth
  // header so a user switch busts it.
  static _homeCountryCache = { key: null, promise: null, ts: 0 };
  static HOME_COUNTRY_TTL_MS = 60 * 60 * 1000;

  static _getHomeCountryCode() {
    const headers = AuthService.getJsonHeaders();
    const key = headers.Authorization || null;
    if (!key) return Promise.resolve(null);

    const cache = this._homeCountryCache;
    if (cache.key === key && cache.promise && Date.now() - cache.ts < this.HOME_COUNTRY_TTL_MS) {
      return cache.promise;
    }

    const promise = (async () => {
      try {
        const response = await fetch(`${API_BASE}/api/preferences/home-country`, { headers });
        if (!response.ok) return null;
        const data = await response.json();
        return data.country_code || null;
      } catch {
        return null; // fall back to Celsius
      }
    })();
    this._homeCountryCache = { key, promise, ts: Date.now() };
    return promise;
  }

  // Get resolved temperature unit for the current user
  static async getTemperatureUnit(userId = null) {
    try {
      const [settings, homeCountryCode] = await Promise.all([
        this.getUserSettings(userId),
        this._getHomeCountryCode()
      ]);
      return this.resolveTemperatureUnit(settings, homeCountryCode);
    } catch (error) {
      console.error('Error getting temperature unit:', error);
      return 'C';
    }
  }

  static resolveDistanceUnit(settings, homeCountryCode = null) {
    const unit = settings?.units?.distance || 'auto';
    if (unit === 'metric' || unit === 'imperial') return unit;
    if (homeCountryCode && this.IMPERIAL_DISTANCE_COUNTRIES.includes(homeCountryCode.toUpperCase())) {
      return 'imperial';
    }
    return 'metric';
  }

  // Get resolved distance unit for the current user
  static async getDistanceUnit(userId = null) {
    try {
      const [settings, homeCountryCode] = await Promise.all([
        this.getUserSettings(userId),
        this._getHomeCountryCode()
      ]);
      return this.resolveDistanceUnit(settings, homeCountryCode);
    } catch (error) {
      console.error('Error getting distance unit:', error);
      return 'metric';
    }
  }

  // Update units settings
  static async updateUnits(unitsUpdates, userId = null) {
    try {
      return await this.updateSettingsFromServer((current) => ({
        ...current,
        units: { ...current.units, ...unitsUpdates }
      }), userId);
    } catch (error) {
      console.error('Error updating units settings:', error);
      return { success: false, error: error.message };
    }
  }

  // Get the account's explicit UI-language preference ('en'|'nl'), or null if
  // the user has never chosen one (i.e. follow device/browser detection).
  static async getLanguage(userId = null) {
    try {
      const settings = await this.getUserSettings(userId);
      return settings.language || null;
    } catch (error) {
      console.error('Error getting language preference:', error);
      return null;
    }
  }

  // Persist the account's UI-language choice. Mirrors updateUnits.
  static async updateLanguage(language, userId = null) {
    try {
      return await this.updateSettingsFromServer((current) => ({ ...current, language }), userId);
    } catch (error) {
      console.error('Error updating language preference:', error);
      return { success: false, error: error.message };
    }
  }

  // Get user's home location from settings
  static async getHomeLocation(userId = null) {
    try {
      const settings = await this.getUserSettings(userId);
      return settings.location?.home || 'Location not set';
    } catch (error) {
      console.error('Error getting home location:', error);
      return 'Location not set';
    }
  }

  // === GENERAL INTERESTS METHODS ===

  // Get default general interests
  static getDefaultGeneralInterests() {
    return {
      museums: 1,      // Default to "I like this"
      dining: 1,
      outdoor: 1,
      wellness: 1,
      family: 1,
      nightlife: 1,
      shopping: 1,
      classes: 1
    };
  }

  // Get user's general interests from database
  // Accepts optional prefetched preferences to avoid redundant API calls
  static async getGeneralInterests(userId = null, prefetchedPrefs = null) {
    try {
      // Get current user if no userId provided
      if (!userId) {
        const currentUser = await AuthService.getCurrentUser();
        userId = currentUser?.id;
      }

      if (!userId) {
        console.warn('No user ID available, returning default general interests');
        return this.getDefaultGeneralInterests();
      }

      // Use prefetched preferences if provided, otherwise load from database
      const dbPreferences = prefetchedPrefs || await UserPreferencesService.getUserPreferencesAsync(userId);

      if (dbPreferences && dbPreferences.general_interests && dbPreferences.general_interests.all) {
        return dbPreferences.general_interests.all;
      }

      // Check localStorage for migration (fallback)
      const localStorageInterests = localStorage.getItem('generalInterests');
      if (localStorageInterests) {
        try {
          const parsed = JSON.parse(localStorageInterests);
          // Auto-migrate to database
          await this.saveGeneralInterests(parsed, userId);
          // Clear localStorage after successful migration
          localStorage.removeItem('generalInterests');
          return parsed;
        } catch (e) {
          console.error('Error parsing localStorage general interests:', e);
        }
      }

      // Return defaults if nothing in database or localStorage
      return this.getDefaultGeneralInterests();
    } catch (error) {
      console.error('Error loading general interests from database:', error);
      return this.getDefaultGeneralInterests();
    }
  }

  // Save user's general interests to database
  static async saveGeneralInterests(interests, userId = null) {
    try {
      // Get current user if no userId provided
      if (!userId) {
        const currentUser = await AuthService.getCurrentUser();
        userId = currentUser?.id;
      }

      if (!userId) {
        console.error('Cannot save general interests without user ID');
        return { success: false, error: 'No user logged in' };
      }

      // Save to database under 'general_interests' category
      const result = await UserPreferencesService.setPreferenceAsync('general_interests', 'all', interests, userId);

      if (result && result.success !== false) {
        return { success: true };
      } else {
        return { success: false, error: result?.error || 'Failed to save general interests' };
      }
    } catch (error) {
      console.error('Error saving general interests to database:', error);
      return { success: false, error: error.message };
    }
  }
}

export default SettingsService;